import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Loader2, RefreshCw } from 'lucide-react';
import { diagnosticsAPI } from '../../lib/api';

export function SupplierClientNameBackfillPanel() {
  const [preview, setPreview] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);

  const runPreview = async () => {
    setPreviewLoading(true);
    setApplyResult(null);
    try {
      const res = await diagnosticsAPI.supplierClientNameBackfillPreview();
      setPreview(res.data);
    } catch {
      setPreview({ error: 'Failed to run preview.' });
    } finally {
      setPreviewLoading(false);
    }
  };

  const runApply = async () => {
    if (!window.confirm(
      `Copy supplier_or_client onto supplier_client_name for ${preview?.corrections_needed || 0} invoices? ` +
      'Non-destructive: the old field is never deleted.'
    )) return;
    setApplyLoading(true);
    try {
      const res = await diagnosticsAPI.supplierClientNameBackfillApply();
      setApplyResult(res.data);
    } catch {
      setApplyResult({ error: 'Failed to apply backfill.' });
    } finally {
      setApplyLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <RefreshCw className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Supplier/Client Name Backfill</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground">
          Invoices created via the manual add-invoice form before the field-name
          unification carry the supplier/client name only under the retired
          supplier_or_client key. Copies it onto supplier_client_name, the
          field every other read site (and every ingestion source) uses.
          Non-destructive. Two-step: Preview, then Apply.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={runPreview}
            disabled={previewLoading}
          >
            {previewLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            Preview
          </Button>
          {preview && !preview.error && preview.corrections_needed > 0 && (
            <Button
              variant="default"
              size="sm"
              onClick={runApply}
              disabled={applyLoading || !!applyResult}
            >
              {applyLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Apply {preview.corrections_needed} Corrections
            </Button>
          )}
        </div>

        {preview && (
          <>
            {preview.error ? (
              <p className="text-xs text-red-600">{preview.error}</p>
            ) : (
              <div className="space-y-2">
                <div className="flex flex-wrap gap-3 text-xs">
                  <span>
                    Corrections needed:{' '}
                    <strong className={preview.corrections_needed > 0 ? 'text-amber-600' : ''}>
                      {preview.corrections_needed}
                    </strong>
                  </span>
                </div>

                {preview.samples?.length > 0 && (
                  <div className="border rounded overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="text-left px-2 py-1 font-medium">Invoice #</th>
                          <th className="text-left px-2 py-1 font-medium">Entity</th>
                          <th className="text-left px-2 py-1 font-medium">Would become</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.samples.map((s, i) => (
                          <tr key={i} className="border-b last:border-0">
                            <td className="px-2 py-1 font-mono">{s.invoice_number || '-'}</td>
                            <td className="px-2 py-1">{s.entity_id || '-'}</td>
                            <td className="px-2 py-1">{s.would_become_supplier_client_name || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {applyResult && (
          <div className={`p-3 rounded text-xs border ${applyResult.error ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
            {applyResult.error ? (
              <p className="text-red-600">{applyResult.error}</p>
            ) : (
              <div className="space-y-1">
                <p><strong>Applied:</strong> {applyResult.applied} invoices corrected</p>
                {applyResult.applied_samples?.length > 0 && (
                  <div className="mt-1">
                    <span className="font-medium">Samples:</span>
                    {applyResult.applied_samples.map((s, i) => (
                      <span key={i} className="ml-1 font-mono">
                        {s.invoice_number || s.id}: {s.supplier_client_name}
                        {i < applyResult.applied_samples.length - 1 ? ',' : ''}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
